<script setup lang="ts">
import { mdiSort, mdiSortAscending, mdiSortDescending } from "@mdi/js";
import type { Header } from "@tanstack/vue-table";
import { computed } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import Button from "../button/Button.vue";
import DropdownMenu from "../dropdown-menu/DropdownMenu.vue";
import DropdownMenuItem from "../dropdown-menu/DropdownMenuItem.vue";
import Icon from "../icon/Icon.vue";

interface Props extends ClassProps {
	header: Header<any, any>;
}
const { header, ...props } = defineProps<Props>();

const sortIcon = computed(() => {
	switch (header.column.getIsSorted()) {
		case "asc":
			return mdiSortAscending;
		case "desc":
			return mdiSortDescending;
		case false:
			return mdiSort;
	}
});

const isSorted = computed(() => header.column.getIsSorted());

function toggleSorting(desc: boolean) {
	const wantedDir = desc ? "desc" : "asc";

	if (isSorted.value === wantedDir) {
		return header.column.clearSorting();
	} else {
		return header.column.toggleSorting(desc);
	}
}
</script>

<template>
	<div v-if="!header.column.getCanSort()" :class="cn('text-xs', props.class)">
		<slot />
	</div>

	<div v-else :class="cn('flex items-center space-x-2', props.class)">
		<DropdownMenu>
			<template #trigger="{ open }">
				<Button
					:class="{
						'-ml-3 h-8': true,
						'btn-active': header.column.getIsSorted() || open,
					}"
					:variant="header.column.getIsSorted() || open ? 'soft' : 'ghost'"
					color="default"
					size="sm"
				>
					<span>
						<slot />
					</span>

					<Icon :d="sortIcon" />
				</Button>
			</template>

			<DropdownMenuItem
				:class="isSorted === 'asc' ? 'bg-focus' : ''"
				@select="toggleSorting(false)"
			>
				<Icon class="text-base-content/50" size="sm" :d="mdiSortAscending" />
				Asc
			</DropdownMenuItem>

			<DropdownMenuItem
				:class="isSorted === 'desc' ? 'bg-focus' : ''"
				@select="toggleSorting(true)"
			>
				<Icon class="text-base-content/50" size="sm" :d="mdiSortDescending" />
				Desc
			</DropdownMenuItem>
		</DropdownMenu>
	</div>
</template>
